import { defineStore } from 'pinia'
import {
  Coin,
  toUtf8,
  toBase64,
  ExchangeModuleParams,
  WasmContractQueryResponse
} from '@injectivelabs/sdk-ts'
import { sharedToBalanceInWei } from '@shared/utils/formatter'
import { wasmApi, exchangeApi, tokenFactoryApi } from '@shared/Service'
import { injToken } from '@shared/data/token'
import { MITO_MASTER_CONTRACT_ADDRESS } from '@/app/utils/constant'

const DEFAULT_TOKEN_CREATION_FEE = sharedToBalanceInWei({
  value: '10'
}).toFixed()
const DEFAULT_SPOT_MARKET_LISTING_FEE = sharedToBalanceInWei({
  value: '20'
}).toFixed()

type ParamStoreState = {
  exchangeModuleParams: ExchangeModuleParams
  denomCreationFee: string
  lpTokenCreationFee: string
  spotMarketInstantListingFee: string
  minPermissionlessVaultInitialFunds: string
  permissionlessVaultRegistrationFee: string
}

const initialStateFactory = (): ParamStoreState => ({
  exchangeModuleParams: {} as ExchangeModuleParams,
  denomCreationFee: '',
  lpTokenCreationFee: '',
  spotMarketInstantListingFee: '',
  minPermissionlessVaultInitialFunds: '',
  permissionlessVaultRegistrationFee: ''
})

export const useParamStore = defineStore('param', {
  state: (): ParamStoreState => initialStateFactory(),
  getters: {
    spotMarketInstantListingFee: (state) => {
      return (
        state.exchangeModuleParams?.spotMarketInstantListingFee?.amount ||
        DEFAULT_SPOT_MARKET_LISTING_FEE
      )
    }
  },
  actions: {
    async fetchTokenCreationFee() {
      const paramStore = useParamStore()

      const { denomCreationFee } = await tokenFactoryApi.fetchModuleParams()

      const [fee] = denomCreationFee
      const feeInToken = fee?.amount || DEFAULT_TOKEN_CREATION_FEE

      paramStore.$patch({
        lpTokenCreationFee: feeInToken
      })
    },

    async fetchExchangeModuleParams() {
      const paramStore = useParamStore()

      const exchangeModuleParams = await exchangeApi.fetchModuleParams()

      paramStore.$patch({
        exchangeModuleParams
      })
    },

    async fetchMitoPermissionlessVaultFee() {
      const paramStore = useParamStore()

      const queryConfigPayload = toBase64({ config: {} })

      const response = (await wasmApi.fetchSmartContractState(
        MITO_MASTER_CONTRACT_ADDRESS,
        queryConfigPayload
      )) as WasmContractQueryResponse

      const config = JSON.parse(toUtf8(response.data)) as {
        permissionless_vault_registration_inj_fee: string
        min_permissionless_vault_initial_funds: Coin[]
      }

      const permissionlessVaultRegistrationFee =
        config.permissionless_vault_registration_inj_fee

      const minPermissionlessVaultInitialFunds =
        config.min_permissionless_vault_initial_funds.find(
          ({ denom }) => denom === injToken.denom
        )

      paramStore.$patch({
        minPermissionlessVaultInitialFunds:
          minPermissionlessVaultInitialFunds?.amount || '0',
        permissionlessVaultRegistrationFee:
          permissionlessVaultRegistrationFee || '0'
      })
    }
  }
})
